import { Card, BlockStack, Text, Box, InlineStack, Badge, Spinner, Banner } from '@shopify/polaris';
import { useQuery } from '@tanstack/react-query';
import { listAudit, type AuditListParams } from '../api/audit';
import { DiffRenderer } from './DiffRenderer';

interface Props {
  entityTable?: string;
  entityId?: number;
  /** Embeds in a Card when true (default). Set false for standalone page. */
  wrapInCard?: boolean;
  limit?: number;
}

export function AuditLogPanel({ entityTable, entityId, wrapInCard = true, limit = 20 }: Props) {
  const params: AuditListParams = { entity_table: entityTable, entity_id: entityId, limit };
  const { data, isLoading, error } = useQuery({
    queryKey: ['audit', params],
    queryFn: () => listAudit(params),
  });

  const content = (
    <BlockStack gap="300">
      <InlineStack align="space-between" blockAlign="center">
        <Text as="h3" variant="headingMd">Audit history</Text>
        {data && <Text as="span" variant="bodySm" tone="subdued">{data.meta.total} total</Text>}
      </InlineStack>
      {isLoading && <Spinner size="small" />}
      {error && <Banner tone="critical">Failed to load audit log: {(error as Error).message}</Banner>}
      {data && data.data.length === 0 && (
        <Text as="p" variant="bodySm" tone="subdued">No audit entries yet.</Text>
      )}
      {data && data.data.map((row) => (
        <Box key={row.id} padding="300" borderWidth="025" borderColor="border" borderRadius="200">
          <BlockStack gap="200">
            <InlineStack gap="200" blockAlign="center">
              <Badge tone={actionTone(row.action)}>{row.action}</Badge>
              <Text as="span" variant="bodySm" fontWeight="semibold">{row.entity_table}#{row.entity_id}</Text>
              <Text as="span" variant="bodySm" tone="subdued">by {row.actor}</Text>
              <Text as="span" variant="bodySm" tone="subdued">{formatDate(row.created_at)}</Text>
            </InlineStack>
            <DiffRenderer diff={row.diff} />
          </BlockStack>
        </Box>
      ))}
    </BlockStack>
  );

  return wrapInCard ? <Card>{content}</Card> : content;
}

function actionTone(a: string): 'success' | 'info' | 'warning' | 'critical' | 'attention' {
  if (a === 'create') return 'success';
  if (a === 'update') return 'info';
  if (a === 'delete') return 'critical';
  if (a === 'import') return 'attention';
  return 'info';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
